// List of blacklisted addresses that might cause performance issues
export const BLACKLISTED_ADDRESSES: string[] = [
  "vitalik.eth",
  "pranksy.eth",
  "0xd8da6bf26964af9d7eed9e03e53415d37aa96045", // vitalik's address
  "0xf476cd75be8fdd197ae0b466a2ec2ae44da41897", // pranksy's address
  // Add more problematic addresses as needed
]

/**
 * Check if an address is blacklisted
 * @param address ENS name or Ethereum address to check
 * @returns Boolean indicating if the address is blacklisted
 */
export function isBlacklistedAddress(address: string): boolean {
  // Normalize the address
  const normalizedAddress = address.toLowerCase()

  // List of blacklisted addresses (can be expanded as needed)
  const blacklistedAddresses = [
    // Add specific addresses here if needed
  ]

  // Check if the address is in the blacklist
  if (blacklistedAddresses.includes(normalizedAddress)) {
    return true
  }

  return false
}

/**
 * Creates a promise that rejects after a specified timeout
 * @param ms Timeout in milliseconds
 * @returns A promise that rejects after the timeout
 */
export function createTimeout(ms: number): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Operation timed out after ${ms}ms`))
    }, ms)
  })
}

/**
 * Executes a promise with a timeout
 * @param promise The promise to execute
 * @param timeoutMs Timeout in milliseconds
 * @returns The result of the promise if it completes before the timeout
 * @throws Error if the timeout is reached before the promise completes
 */
export function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`Operation timed out after ${timeoutMs}ms`))
    }, timeoutMs)

    promise
      .then((result) => {
        clearTimeout(timeoutId)
        resolve(result)
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        reject(error)
      })
  })
}

// Add this function to check if a wallet has too many NFTs
export async function hasTooManyNFTs(walletAddress: string): Promise<boolean> {
  try {
    // Use Alchemy API to get the total NFT count
    const ALCHEMY_API_KEY = process.env.ALCHEMY_API_KEY || process.env.NEXT_PUBLIC_ALCHEMY_API_KEY || "demo"
    const url = `https://eth-mainnet.g.alchemy.com/nft/v2/${ALCHEMY_API_KEY}/getNFTs/?owner=${walletAddress}&pageSize=1&withMetadata=false`

    const response = await fetch(url)
    if (!response.ok) {
      console.error(`Alchemy API error: ${response.status}`)
      return false // If we can't check, assume it's safe to proceed
    }

    const data = await response.json()
    console.log(`Wallet ${walletAddress} has ${data.totalCount} NFTs`)

    // Return true if the wallet has more than 3000 NFTs
    return data.totalCount > 3000
  } catch (error) {
    console.error("Error checking NFT count:", error)
    return false // If we can't check, assume it's safe to proceed
  }
}
